// The trips dashboard, rebuilt to "Dashboard - Field Notes.dc.html".
//
// Trips are polaroids on a shelf: taped, slightly rotated, straightening and
// lifting on hover. Status stamps, the "N stops" count and the handwritten
// note line are all derived from real trip data rather than invented.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BadgeCheck, Compass, Plus, Trash2 } from "lucide-react";
import TravelPreferences from "@/components/TravelPreferences";
import { useJourneys, type Trip } from "@/context/JourneyContext";
import { useAuth } from "@/context/AuthContext";
import { getCoverImage } from "@/lib/trip-helpers";
import { useToast } from "@/hooks/use-toast";

const ANGLES = [
  { rot: -1, tape: 1.8 },
  { rot: 0.8, tape: -2 },
  { rot: -0.6, tape: 1.4 },
  { rot: 1.2, tape: -1.2 },
  { rot: -1.4, tape: 2.2 },
  { rot: 0.6, tape: -1.6 },
];

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_LONG = ["January","February","March","April","May","June","July",
  "August","September","October","November","December"];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// "Sep 4 – 11" within a month, "Sep 28 – Oct 2" across one.
const formatRange = (startDate?: string, endDate?: string): string => {
  if (!startDate || !endDate) return "No dates yet";
  const a = new Date(startDate), b = new Date(endDate);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "No dates yet";
  const left = `${MONTHS_SHORT[a.getMonth()]} ${a.getDate()}`;
  const right = a.getMonth() === b.getMonth() ? `${b.getDate()}` : `${MONTHS_SHORT[b.getMonth()]} ${b.getDate()}`;
  return `${left} – ${right}`;
};

// The stamp the design puts on each polaroid, derived from the trip's own
// dates: an accent stamp for what is coming up, a paper stamp otherwise.
const statusFor = (trip: Trip): { label: string; accent: boolean } => {
  const today = startOfDay(new Date());
  if (!trip.startDate) return { label: "Drafting", accent: false };
  const start = startOfDay(new Date(trip.startDate));
  const end = trip.endDate ? startOfDay(new Date(trip.endDate)) : start;
  if (Number.isNaN(start.getTime())) return { label: "Drafting", accent: false };
  if (end < today) {
    return { label: `Traveled · ${MONTHS_LONG[end.getMonth()]} ${end.getFullYear()}`, accent: false };
  }
  if (start <= today) return { label: "Travelling now", accent: true };
  const days = Math.round((start.getTime() - today.getTime()) / 86400000);
  return {
    label: days === 1 ? "Up next · tomorrow" : `Up next · in ${days} days`,
    accent: true,
  };
};

const Dashboard = () => {
  const { journeys, journeysLoading, deleteJourney, clearAllJourneys } = useJourneys();
  const { user } = useAuth();
  const { toast } = useToast();

  const [deleteTarget, setDeleteTarget] = useState<Trip | null>(null);
  const [showClearAll, setShowClearAll] = useState(false);
  const [showClearLocal, setShowClearLocal] = useState(false);

  // localStorage.clear() wipes everything for the origin (trips, preferences,
  // consent, theme, dismissals). Reload so React reflects the empty slate.
  const wipeLocalData = () => {
    try {
      localStorage.clear();
    } catch {
      /* ignore */
    }
    window.location.href = "/";
  };

  const handleDelete = (trip: Trip) => {
    deleteJourney(trip.id);
    setDeleteTarget(null);
    toast({ title: "Trip deleted", description: `${trip.title || trip.destination} removed.` });
  };

  const handleClearAll = () => {
    const count = journeys.length;
    clearAllJourneys();
    setShowClearAll(false);
    toast({
      title: "All trips deleted",
      description: `${count} ${count === 1 ? "trip" : "trips"} removed.`,
    });
  };

  const cards = useMemo(
    () =>
      journeys.map((trip, i) => {
        const items = trip.days.flatMap((d) => d.items);
        const noteCount = items.filter((it) => it.notes?.trim()).length;
        return {
          trip,
          angle: ANGLES[i % ANGLES.length],
          title: trip.title || trip.destination || "Untitled trip",
          no: `Nº ${journeys.length - i}`,
          dates: formatRange(trip.startDate, trip.endDate),
          days: `${trip.days.length} ${trip.days.length === 1 ? "day" : "days"}`,
          stops: items.length ? `${items.length} ${items.length === 1 ? "stop" : "stops"}` : null,
          note: noteCount
            ? `${noteCount} ${noteCount === 1 ? "note" : "notes"} scribbled`
            : null,
          status: statusFor(trip),
        };
      }),
    [journeys]
  );

  const hasTrips = cards.length > 0;
  const kicker = journeysLoading
    ? "The collection"
    : `The collection · ${journeys.length} ${journeys.length === 1 ? "expedition" : "expeditions"}`;

  const footerNote = user
    ? "Your trips are saved to your account and sync across devices. This clears cached data and settings from this browser only."
    : "Trips and preferences live only in this browser. Clearing them removes all Sunday Atlas data from this device.";

  const confirm =
    deleteTarget
      ? {
          kicker: "Delete trip",
          title: `Delete ${deleteTarget.title || deleteTarget.destination || "this trip"}?`,
          body: "This removes the trip and its notes. This cannot be undone.",
          cta: "Delete trip",
          onConfirm: () => handleDelete(deleteTarget),
          close: () => setDeleteTarget(null),
        }
      : showClearAll
        ? {
            kicker: "Clear all trips",
            title: `Delete all ${journeys.length} trips?`,
            body: `Delete all ${journeys.length} trips? This cannot be undone.`,
            cta: "Delete all",
            onConfirm: handleClearAll,
            close: () => setShowClearAll(false),
          }
        : showClearLocal
          ? {
              kicker: "Clear local data",
              title: "Clear this browser?",
              body: user
                ? "Cached trips and settings are removed from this browser. Your account keeps everything."
                : "All Sunday Atlas data on this device is removed. This cannot be undone.",
              cta: "Clear data",
              onConfirm: wipeLocalData,
              close: () => setShowClearLocal(false),
            }
          : null;

  return (
    <div className="mx-auto max-w-[1200px] px-5 md:px-12 pt-10 md:pt-14 pb-14 md:pb-[72px]">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-3">
        <div>
          <div className="font-sans text-xs font-semibold uppercase tracking-[0.22em] text-ink-muted mb-3.5">
            {kicker}
          </div>
          <h1 className="relative inline-block font-serif text-[38px] md:text-[52px] leading-[1.05]">
            Your trips
            <svg
              viewBox="0 0 300 14"
              fill="none"
              aria-hidden="true"
              className="absolute left-0 -bottom-2.5 w-full h-3.5"
            >
              <path d="M4 9 C60 3, 150 11, 296 5" stroke="var(--fn-accent)" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </h1>
        </div>

        <div className="flex items-center gap-[22px]">
          {hasTrips && !journeysLoading && (
            <button
              type="button"
              onClick={() => setShowClearAll(true)}
              className="rule-link font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted hover:text-danger transition-colors"
              style={{ backgroundImage: "linear-gradient(var(--fn-rule-dash-2), var(--fn-rule-dash-2))" }}
            >
              Clear all trips
            </button>
          )}
          <Link
            to="/journey/new"
            className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-[26px] py-3.5 min-h-[44px] font-sans text-[11px] font-bold uppercase tracking-[0.16em] hover:bg-accent-text hover:text-on-accent transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Plan a new trip
          </Link>
        </div>
      </div>

      <div className="my-9 md:mb-11">
        <TravelPreferences />
      </div>

      {journeysLoading ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <div className="relative" style={{ transform: `rotate(${[-1, 0.8, -0.6][i]}deg)` }}>
                  <div
                    className="w-full h-[300px] border border-rule box-border"
                    style={{
                      background:
                        "repeating-linear-gradient(45deg, var(--fn-map) 0, var(--fn-map) 8px, var(--fn-stripe-b) 8px, var(--fn-stripe-b) 16px)",
                    }}
                  />
                  <span className="absolute -top-2.5 left-1/2 bg-tape w-[76px] h-[22px] block shadow-[0_1px_2px_rgba(0,0,0,0.08)]" style={{ transform: "translateX(-50%) rotate(1.8deg)" }} />
                </div>
                <div className="border-b border-ink pt-4 pb-3.5 px-0.5 flex flex-col gap-2.5">
                  <div className="flex justify-between">
                    <span className="h-4 bg-skeleton block" style={{ width: [180, 140, 160][i] }} />
                    <span className="w-8 h-2.5 bg-skeleton block" />
                  </div>
                  <span className="w-[150px] h-[9px] bg-skeleton block" />
                </div>
              </div>
            ))}
          </div>
          <div className="text-center hand text-[19px] text-ink-faint mt-9">fetching your shelf...</div>
        </>
      ) : !hasTrips ? (
        <div className="border border-dashed border-rule-dash py-[72px] px-8 flex flex-col items-center text-center">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-accent-tint mb-[22px]">
            <Compass className="h-[26px] w-[26px] text-accent" />
          </span>
          <h2 className="font-serif text-[32px] mb-2.5">Where to first?</h2>
          <p className="font-serif text-[15.5px] leading-[1.6] text-ink-muted mb-[26px] max-w-[380px]">
            Tell the concierge a city, a month, or just a mood. It drafts the days and checks every place.
          </p>
          <Link
            to="/journey/new"
            className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-[26px] py-3.5 min-h-[44px] font-sans text-[11px] font-bold uppercase tracking-[0.16em] hover:text-accent-on-dark transition-colors"
          >
            Plan a trip
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {cards.map((c) => (
              <div key={c.trip.id} className="group relative">
                <Link to={`/journey/${c.trip.id}`} className="block">
                  <span
                    className="relative block transition-transform duration-200 group-hover:rotate-0 group-hover:-translate-y-1"
                    style={{ transform: `rotate(${c.angle.rot}deg)` }}
                  >
                    <span
                      role="img"
                      aria-label={c.title}
                      className="block w-full h-[300px] bg-cover bg-center bg-map border border-rule box-border"
                      style={{ backgroundImage: `url(${c.trip.coverImage || getCoverImage(c.trip.destination || c.trip.title)})` }}
                    />
                    <span
                      aria-hidden="true"
                      className="absolute -top-2.5 left-1/2 bg-tape w-[76px] h-[22px] block shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                      style={{ transform: `translateX(-50%) rotate(${c.angle.tape}deg)` }}
                    />
                    {c.status.accent ? (
                      <span className="absolute top-3 right-3 font-sans text-[9.5px] font-bold uppercase tracking-[0.14em] bg-accent-text text-on-accent px-2.5 py-1.5">
                        {c.status.label}
                      </span>
                    ) : (
                      <span className="absolute bottom-3 left-3 font-sans text-[9.5px] font-bold uppercase tracking-[0.14em] bg-paper border border-rule text-ink-muted px-2.5 py-1.5">
                        {c.status.label}
                      </span>
                    )}
                  </span>

                  <span className="block border-b border-ink pt-3.5 pb-3 px-0.5">
                    <span className="flex justify-between items-baseline gap-2.5">
                      <span className="font-serif text-2xl text-ink">{c.title}</span>
                      <span className="font-serif italic text-sm text-ink-faint whitespace-nowrap">{c.no}</span>
                    </span>
                    <span className="flex gap-4 mt-1.5 font-sans text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted flex-wrap">
                      <span>{c.dates}</span>
                      <span>{c.days}</span>
                      {c.stops && (
                        <span className="text-accent-text inline-flex items-center gap-1">
                          <BadgeCheck className="h-[11px] w-[11px]" />
                          {c.stops}
                        </span>
                      )}
                    </span>
                  </span>

                  {c.note && (
                    <span className="block hand text-[17px] text-ink-muted pt-2 px-0.5">{c.note}</span>
                  )}
                </Link>

                {/* Hover-revealed on a pointer device, but focus-visible keeps
                    it reachable by keyboard, which the prototype does not. */}
                <button
                  type="button"
                  onClick={() => setDeleteTarget(c.trip)}
                  aria-label={`Delete ${c.title}`}
                  title="Delete trip"
                  className="group/del absolute -top-[9px] -right-[9px] h-11 w-11 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                >
                  {/* 44px transparent hit box, 30px painted circle inside, so
                      the visual matches the design and touch still clears 44. */}
                  <span className="h-[30px] w-[30px] rounded-full border-2 border-paper bg-ink text-paper inline-flex items-center justify-center transition-colors group-hover/del:bg-danger">
                    <Trash2 className="h-[13px] w-[13px]" />
                  </span>
                </button>
              </div>
            ))}
          </div>

          <div className="mt-14 border border-dashed border-rule-dash p-7 flex items-center justify-between gap-5 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="hand text-[22px] text-accent-text -rotate-2">where to next?</span>
              <span className="font-serif text-[15px] text-ink-muted">
                Tell the concierge a city, a month, or just a mood.
              </span>
            </div>
            <Link
              to="/journey/new"
              className="inline-flex items-center gap-2 rounded-full border border-ink px-6 py-3 min-h-[44px] font-sans text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-ink hover:text-paper transition-colors"
            >
              Start planning
            </Link>
          </div>
        </>
      )}

      <div className="mt-12 pt-5 border-t border-rule flex items-center justify-between gap-3 flex-wrap">
        <p className="font-serif italic text-[13px] text-ink-faint max-w-[560px]">{footerNote}</p>
        <button
          type="button"
          onClick={() => setShowClearLocal(true)}
          className="font-sans text-[10px] font-bold uppercase tracking-[0.14em] text-ink-faint hover:text-danger transition-colors h-11 -my-3.5 inline-flex items-center"
        >
          Clear all local data
        </button>
      </div>

      {/* One sheet drives all three confirmations, as in the prototype. */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && confirm?.close()}>
        <DialogContent className="max-w-[420px] rounded-none bg-paper border-rule p-7 pb-6 gap-0 [&>button.absolute]:hidden">
          {confirm && (
            <>
              <div className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-danger mb-2.5">
                {confirm.kicker}
              </div>
              <h3 className="font-serif text-[26px] leading-[1.15] mb-2.5">{confirm.title}</h3>
              <p className="font-serif text-[14.5px] leading-[1.6] text-ink-muted mb-[22px]">{confirm.body}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={confirm.onConfirm}
                  className="flex-1 font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] bg-danger text-on-accent rounded-full py-3.5 min-h-[44px]"
                >
                  {confirm.cta}
                </button>
                <button
                  type="button"
                  onClick={confirm.close}
                  className="font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] border border-rule-dash-2 rounded-full px-[18px] py-3.5 min-h-[44px] text-ink-muted hover:border-ink hover:text-ink transition-colors"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
